/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from "@emotion/react";

import { MouseEventHandler, ReactNode } from "react";

import { useTranslation } from "react-i18next";

import Typography from "@mui/material/Typography";

import {
  AlertIcon,
  CheckCircleIcon,
  CloseIcon,
  colors,
  InfoIcon,
  WarningIcon
} from "@dotnet/telematics-design-system";

import TextLink from "../TextLink/TextLink";
import { TEXT_LINK_SIZES } from "../TextLink/types";
import { useStyles } from "./styles";
import { BANNER_MESSAGE_STATUSES } from "./types";

export interface BannerMessageProps {
  status: BANNER_MESSAGE_STATUSES;
  title?: string | ReactNode;
  description?: string | ReactNode;
  css?: SerializedStyles;
  textWidth?: string;
  externalCustomStyle?: any;
  testId?: string;
  expandable?: boolean;
  showMoreText?: boolean;
  setShowMoreText?: any;
  collapsedDescription?: string | ReactNode;
  externalAction?: string | ReactNode;
  close?: MouseEventHandler<SVGSVGElement>;
}

const BannerMessage = ({
  status,
  title,
  description,
  textWidth,
  externalCustomStyle,
  testId,
  expandable,
  showMoreText,
  setShowMoreText,
  collapsedDescription,
  externalAction,
  close,
  ...props
}: BannerMessageProps) => {
  const {
    blue100,
    blue500,
    blue700,
    red50,
    red300,
    red700,
    orange50,
    orange300,
    orange600,
    green600,
    green50,
    green300,
    textDark
  } = colors;

  const { t } = useTranslation();

  const { bannerMessage, bannerMessageContent, closeBannerIcon } = useStyles();

  const iconSize = 16;

  const styleOptions = {
    info: {
      background: blue100,
      border: blue500,
      icon: <InfoIcon color={blue700} width={iconSize} height={iconSize} />
    },
    critical: {
      background: red50,
      border: red300,
      icon: <AlertIcon color={red700} width={iconSize} height={iconSize} />
    },
    warning: {
      background: orange50,
      border: orange300,
      icon: <WarningIcon color={orange600} width={iconSize} height={iconSize} />
    },
    success: {
      background: green50,
      border: green300,
      icon: (
        <CheckCircleIcon color={green600} width={iconSize} height={iconSize} />
      )
    }
  };

  const bgColor = styleOptions[status].background;
  const borderColor = styleOptions[status].border;
  const iconImage = styleOptions[status].icon;

  const handleToggleShowMore = () => {
    setShowMoreText(!showMoreText);
  };

  return (
    <div
      data-testid={testId ?? "banner-message"}
      {...props}
      css={css([
        {
          background: bgColor,
          border: `1px solid ${borderColor}`
        },
        externalCustomStyle,
        bannerMessage
      ])}
    >
      {iconImage}

      <div
        css={css([
          {
            width: textWidth,
            color: textDark
          },
          bannerMessageContent
        ])}
      >
        {title && <Typography variant="body3SemiBold">{title}</Typography>}

        {expandable && (
          <Typography
            variant="body3Regular"
            sx={{ marginTop: title ? "4px" : "0" }}
          >
            {showMoreText ? description : collapsedDescription}
          </Typography>
        )}

        {description && !expandable && (
          <Typography
            variant="body3Regular"
            sx={{ marginTop: title ? "4px" : "0" }}
          >
            {description}
          </Typography>
        )}

        {expandable && (
          <TextLink
            size={TEXT_LINK_SIZES.Small}
            onClick={handleToggleShowMore}
            sx={{ overflowWrap: "break-word" }}
            customStyle={{ paddingTop: "4px" }}
          >
            {showMoreText ? t("Details##show less") : t("Details##show more")}
          </TextLink>
        )}

        {externalAction}
      </div>

      {close && <CloseIcon css={css(closeBannerIcon)} onClick={close} />}
    </div>
  );
};

export { BANNER_MESSAGE_STATUSES } from "./types/enums";

export default BannerMessage;
